import BFLApiFailure from "@repo/shared-ui/BFLApiFailure";
import { FC, ReactNode, Suspense } from "react";
import { StockItem } from "../../types/stocks";
import { getBudgetFriendlyETFs } from "../../services/TradingMallService";
import StockListNotFound from "../ClpStockCards/StockListNotFound";
import BudgetETFMainClient from "./BudgetETFMainClient";
import BudgetEtfHeader from "./BudgetEtfHeader";
import {
  mapResponseToStocks,
  resolveLtpFilter,
  resolveSortForApi,
} from "./budgetEtfUtils";
import {
  BUDGET_ETF_MAIN_AEM_DEFAULTS,
  BudgetEtfMainProps,
} from "./BudgetETFTypes";

const BudgetEtfMain: FC<BudgetEtfMainProps> = async ({
  mainHeading = BUDGET_ETF_MAIN_AEM_DEFAULTS.mainHeading,
  heading = BUDGET_ETF_MAIN_AEM_DEFAULTS.heading,
  description = BUDGET_ETF_MAIN_AEM_DEFAULTS.description,
  defaultSort,
  componentPosition,
  loggedInRedirectionUrl = BUDGET_ETF_MAIN_AEM_DEFAULTS.loggedInRedirectionUrl,
  screen = BUDGET_ETF_MAIN_AEM_DEFAULTS.screen,
  webViewTitle,
  stockNotFoundText = BUDGET_ETF_MAIN_AEM_DEFAULTS.stockNotFoundText,
  stockNotFoundImgUrl = BUDGET_ETF_MAIN_AEM_DEFAULTS.stockNotFoundImgUrl,
  initialLtpFilter,
  errorTitle = BUDGET_ETF_MAIN_AEM_DEFAULTS.errorTitle,
  errorDescription = BUDGET_ETF_MAIN_AEM_DEFAULTS.errorDescription,
  errorRetryButtonText = BUDGET_ETF_MAIN_AEM_DEFAULTS.errorRetryButtonText,
  errorImageAlt = BUDGET_ETF_MAIN_AEM_DEFAULTS.errorImageAlt,
  errorImageSrc = BUDGET_ETF_MAIN_AEM_DEFAULTS.errorImageSrc,
  showMoreText = BUDGET_ETF_MAIN_AEM_DEFAULTS.showMoreText,
  showLessText = BUDGET_ETF_MAIN_AEM_DEFAULTS.showLessText,
  companyName = BUDGET_ETF_MAIN_AEM_DEFAULTS.companyName,
}) => {
  const resolvedLtpFilter = resolveLtpFilter(initialLtpFilter);
  let hasError = false;
  let initialStocks: StockItem[] = [];
  let showMore = false;
  let showLess = false;

  try {
    const response = await getBudgetFriendlyETFs({
      page: 1,
      limit: 10,
      sort: resolveSortForApi("default", defaultSort),
      ltpFilter: resolvedLtpFilter,
    });

    initialStocks = mapResponseToStocks(response);
    showMore = response?.showMore ?? false;
    showLess = response?.showLess ?? false;
  } catch (error) {
    console.error("Failed to load budget friendly ETFs for main page:", error);
    hasError = true;
  }

  const renderShell = (content: ReactNode) => (
    <>
      <p className="hidden border-b-2 border-[#E5EAF7] py-[10px] pl-[24px] text-[14px] font-[400] text-[#222F3F] sm:block">
        {mainHeading}
      </p>
      <div className="flex flex-row">
        <div className="hidden w-[243px] border-r-2 border-[#E5EAF7] sm:block" />
        <div className="flex w-full flex-col gap-3">{content}</div>
      </div>
    </>
  );

  if (hasError && initialStocks.length === 0) {
    return renderShell(
      <>
        <BudgetEtfHeader header={heading} description={description} />
        <div className="flex min-h-[400px] items-center justify-center py-12">
          <BFLApiFailure
            imageSrc={errorImageSrc}
            imageAlt={errorImageAlt}
            error={errorTitle}
            errorDescription={errorDescription}
            retryButtonText={errorRetryButtonText}
          />
        </div>
      </>,
    );
  }

  if (!hasError && initialStocks.length === 0) {
    return renderShell(
      <>
        <BudgetEtfHeader header={heading} description={description} />
        <StockListNotFound
          errorMessage={stockNotFoundText}
          stockNotFoundImgUrl={stockNotFoundImgUrl}
        />
      </>,
    );
  }

  return renderShell(
    <Suspense fallback={null}>
      <BudgetETFMainClient
        initialStocks={initialStocks}
        initialShowMore={showMore}
        initialShowLess={showLess}
        initialLtpFilter={resolvedLtpFilter}
        defaultSort={defaultSort}
        heading={heading}
        description={description}
        loggedInRedirectionUrl={loggedInRedirectionUrl}
        screen={screen}
        webViewTitle={webViewTitle}
        stockNotFoundText={stockNotFoundText}
        stockNotFoundImgUrl={stockNotFoundImgUrl}
        componentPosition={componentPosition}
        showMoreText={showMoreText}
        showLessText={showLessText}
        companyName={companyName}
        mainHeading={mainHeading}
      />
    </Suspense>,
  );
};

export default BudgetEtfMain;
